import type { audience } from '@bb/contracts';
import { RadioGroup } from '@bb/ui';
import { AudienceBuilder, emptyAudience } from '../../audience/AudienceBuilder.tsx';
import { AudiencePopulation } from '../../audience/AudiencePopulation.tsx';
import type { CampaignWizardDraft, WizardIssue } from './steps.ts';

export interface TargetStepProps {
  environmentId: string;
  draft: CampaignWizardDraft;
  onChange: (next: CampaignWizardDraft) => void;
  issues: WizardIssue[];
  readOnly: boolean;
  /** Debounce of the reach estimate while the audience is edited. */
  estimateDebounceMs?: number;
}

/** Everyone in the environment: the reach of a campaign without an audience. */
export const EVERYONE: audience.AudienceDefinition = { schemaVersion: 1, include: { kind: 'and', id: 'root', children: [] }, exclude: [], apps: { mode: 'all' } };

const WHO = [
  { value: 'everyone', label: 'All users' },
  { value: 'audience', label: 'Users who match an audience' },
];

/**
 * Target (BB-97 over BB-40/BB-44/BB-45): who gets the campaign — segments (in a group they intersect) and further
 * filters, exclusions and the apps it covers, all in the audience builder with its User Lookup — and how many users
 * it reaches, by channel, before launch.
 */
export function TargetStep({ environmentId, draft, onChange, issues, readOnly, estimateDebounceMs }: TargetStepProps) {
  const definition = draft.content.audience as audience.AudienceDefinition | null;
  const setAudience = (next: audience.AudienceDefinition | null) => onChange({ ...draft, content: { ...draft.content, audience: next } });
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  return (
    <section aria-label="Target" className="canvas-wizard__panel canvas-wizard__panel--wide">
      <h2>Target audience</h2>
      <RadioGroup label="Who gets the campaign" options={WHO.map((o) => ({ ...o, disabled: readOnly }))} value={definition ? 'audience' : 'everyone'} onValueChange={(who) => setAudience(who === 'audience' ? (definition ?? emptyAudience()) : null)} />
      <p className="hint">Segments in one group must all match (their intersection); add filters next to them to narrow it further. The audience is checked when the campaign sends.</p>
      {errors.map((i) => (
        <p key={i.message} className="bb-field__error" role="alert">
          {i.message}
        </p>
      ))}
      {warnings.map((i) => (
        <p key={i.message} className="notice notice--warning" role="status">
          {i.message}
        </p>
      ))}
      {definition && <AudienceBuilder environmentId={environmentId} value={definition} readOnly={readOnly} onChange={setAudience} />}
      <section aria-label="Reach">
        <h3>Reach</h3>
        <AudiencePopulation environmentId={environmentId} definition={definition ?? EVERYONE} {...(estimateDebounceMs !== undefined ? { debounceMs: estimateDebounceMs } : {})} />
      </section>
    </section>
  );
}
